import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../Card';
import { Badge } from '../Badge';
import { ShieldAlert, ChevronDown, Clock } from 'lucide-react';
type ThreatLevel = 'green' | 'yellow' | 'orange' | 'red' | 'black';
interface NationalThreatLevelProps {
  currentLevel?: ThreatLevel;
}
const tiers = [{
  id: 'green',
  label: 'GREEN',
  desc: 'Normal',
  color: 'bg-emerald-500',
  text: 'text-emerald-500',
  border: 'border-emerald-500',
  glow: 'shadow-[0_0_15px_rgba(16,185,129,0.5)]'
}, {
  id: 'yellow',
  label: 'YELLOW',
  desc: 'Escalating',
  color: 'bg-amber-500',
  text: 'text-amber-500',
  border: 'border-amber-500',
  glow: 'shadow-[0_0_15px_rgba(245,158,11,0.5)]'
}, {
  id: 'orange',
  label: 'ORANGE',
  desc: 'Coordinated Narrative',
  color: 'bg-orange-500',
  text: 'text-orange-500',
  border: 'border-orange-500',
  glow: 'shadow-[0_0_15px_rgba(249,115,22,0.5)]'
}, {
  id: 'red',
  label: 'RED',
  desc: 'National Information Crisis',
  color: 'bg-red-500',
  text: 'text-red-500',
  border: 'border-red-500',
  glow: 'shadow-[0_0_15px_rgba(239,68,68,0.5)]'
}, {
  id: 'black',
  label: 'BLACK',
  desc: 'Presidential Threat',
  color: 'bg-slate-900 dark:bg-slate-100',
  text: 'text-slate-900 dark:text-slate-100',
  border: 'border-slate-900 dark:border-slate-100',
  glow: 'shadow-[0_0_15px_rgba(15,23,42,0.5)] dark:shadow-[0_0_15px_rgba(241,245,249,0.5)]'
}];
const reasons: Record<ThreatLevel, string[]> = {
  green: ['Aktivitas siber dalam batas normal', 'Sentimen publik stabil'],
  yellow: ['Peningkatan volume percakapan negatif 15%', 'Deteksi awal bot farm di 2 wilayah'],
  orange: ['Lonjakan narasi koordinasi di Papua', 'Deteksi deepfake aktif menyasar kabinet', 'Aktivitas aktor asing meningkat 37%'],
  red: ['Serangan siber masif pada infrastruktur kritis', 'Penyebaran hoaks skala nasional tak terkendali', 'Mobilisasi massa via platform digital terdeteksi'],
  black: ['Ancaman fisik langsung terhadap VVIP', 'Kompromi sistem komando utama', 'Kebocoran data intelijen tingkat tinggi']
};
export function NationalThreatLevel({
  currentLevel = 'orange'
}: NationalThreatLevelProps) {
  const activeTier = tiers.find(t => t.id === currentLevel) || tiers[2];
  return <Card className="bg-card border-border/50 overflow-hidden flex flex-col h-full" data-id="element-689">
      <CardHeader className="pb-3 pt-4 px-5 border-b border-border/50 bg-muted/10" data-id="element-690">
        <div className="flex items-center justify-between" data-id="element-691">
          <CardTitle className="font-mono text-sm uppercase tracking-wider text-foreground flex items-center gap-2" data-id="element-692">
            <ShieldAlert className="size-4 text-muted-foreground" data-id="element-693" />
            National Threat Level
          </CardTitle>
          <Badge variant="outline" className="font-mono text-[9px] h-5 border-border/50 bg-background" data-id="element-694">
            DOCTRINE v2.4
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-5 flex-1 flex flex-col gap-6" data-id="element-695">
        {/* Tier Bar */}
        <div className="relative pt-3" data-id="element-696">
          <div className="flex w-full h-12 rounded-md overflow-hidden border border-border/40" data-id="element-697">
            {tiers.map(tier => {
            const isActive = tier.id === currentLevel;
            return <div key={tier.id} className={`flex-1 flex flex-col items-center justify-center relative transition-all duration-500 ${isActive ? `${tier.color} ${tier.glow}` : 'bg-muted/30'}`} data-id="element-698">
                  {isActive && <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-foreground" data-id="element-699">
                      <ChevronDown className="size-4 animate-bounce" data-id="element-700" />
                    </div>}
                  <span className={`font-mono text-[10px] font-bold tracking-wider ${isActive ? tier.id === 'black' ? 'text-white dark:text-slate-900' : 'text-white' : 'text-muted-foreground'}`} data-id="element-701">
                    {tier.label}
                  </span>
                  <span className={`text-[8px] uppercase tracking-wide hidden sm:block ${isActive ? tier.id === 'black' ? 'text-white/80 dark:text-slate-900/80' : 'text-white/80' : 'text-muted-foreground/60'}`} data-id="element-702">
                    {tier.desc}
                  </span>
                </div>;
          })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1" data-id="element-703">
          {/* Current Status Details */}
          <div className="md:col-span-2 flex flex-col gap-3" data-id="element-704">
            <div data-id="element-705">
              <h3 className={`font-mono text-2xl font-bold tracking-tight ${activeTier.text}`} data-id="element-706">
                {activeTier.label}
              </h3>
              <p className="text-sm font-medium text-foreground uppercase tracking-wide" data-id="element-707">
                {activeTier.desc}
              </p>
              <div className="flex items-center gap-1.5 mt-1 text-muted-foreground" data-id="element-708">
                <Clock className="size-3" data-id="element-709" />
                <span className="font-mono text-[10px]" data-id="element-710">
                  Diaktifkan pada 22 Mei 2026 · 14:22 WIB
                </span>
              </div>
            </div>

            <div className="bg-muted/20 border border-border/50 rounded-md p-3 mt-auto" data-id="element-711">
              <p className="font-mono text-[10px] text-muted-foreground mb-2 uppercase tracking-wider" data-id="element-712">
                Pemicu Eskalasi:
              </p>
              <ul className="space-y-1.5" data-id="element-713">
                {reasons[currentLevel].map((reason, idx) => <li key={idx} className="flex items-start gap-2 font-mono text-[11px] text-foreground" data-id="element-714">
                    <span className={`mt-1 size-1.5 rounded-full shrink-0 ${activeTier.color}`} data-id="element-715" />
                    {reason}
                  </li>)}
              </ul>
            </div>
          </div>

          {/* History Timeline */}
          <div className="border-l border-border/50 pl-5 flex flex-col" data-id="element-716">
            <p className="font-mono text-[10px] text-muted-foreground mb-3 uppercase tracking-wider" data-id="element-717">
              Riwayat Eskalasi
            </p>
            <div className="relative flex-1" data-id="element-718">
              <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border/50" data-id="element-719" />
              <div className="space-y-4 relative" data-id="element-720">
                <div className="flex items-start gap-3" data-id="element-721">
                  <div className={`mt-1 size-3 rounded-full shrink-0 z-10 ${activeTier.color} ring-4 ring-background`} data-id="element-722" />
                  <div data-id="element-723">
                    <p className="font-mono text-[10px] font-bold text-foreground" data-id="element-724">
                      {activeTier.label}
                    </p>
                    <p className="font-mono text-[9px] text-muted-foreground" data-id="element-725">
                      Hari ini, 14:22 WIB
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 opacity-60" data-id="element-726">
                  <div className="mt-1 size-3 rounded-full shrink-0 z-10 bg-amber-500 ring-4 ring-background" data-id="element-727" />
                  <div data-id="element-728">
                    <p className="font-mono text-[10px] font-bold text-foreground" data-id="element-729">
                      YELLOW
                    </p>
                    <p className="font-mono text-[9px] text-muted-foreground" data-id="element-730">
                      Hari ini, 09:15 WIB
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 opacity-40" data-id="element-731">
                  <div className="mt-1 size-3 rounded-full shrink-0 z-10 bg-emerald-500 ring-4 ring-background" data-id="element-732" />
                  <div data-id="element-733">
                    <p className="font-mono text-[10px] font-bold text-foreground" data-id="element-734">
                      GREEN
                    </p>
                    <p className="font-mono text-[9px] text-muted-foreground" data-id="element-735">
                      Kemarin, 00:00 WIB
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}